import { type ComponentConfig } from "@puckeditor/core";
import { Hero, type HeroProps } from "./hero";
import { HeroFields } from "./hero.fields";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { ctaPosture, SECONDARY_FORM_CTA_TEXT, contactUrl} from "../../lib/cta-defaults";
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { getWindowJobsiteConfig, type JobsiteBusinessContext } from "../../types/runtime-config";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { ImagePicker } from "../editor/ImagePicker";
import { withBusinessCtaOverride } from "../../lib/business-bound-fields";

/**
 * Puck component configuration for Hero
 * Includes fields, defaultProps, and render function
 */

/**
 * The complement button when the owner's chosen CTA is phone-first.
 *
 * Says nothing the business hasn't: no "free", no "estimate", no promise about
 * response time. The owner's own label always occupies the primary button.
 */

export const HeroConfig: ComponentConfig<HeroProps> = {
  label: "Hero Section",
  fields: {
    headline: {
      ...HeroFields.headline,
      contentEditable: true, // Enable inline editing in preview
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.hero.headline"
          label="Headline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheadline: {
      ...HeroFields.subheadline,
      contentEditable: true, // Enable inline editing in preview
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.hero.subheadline"
          label="Subheadline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    keywordBadge: {
      ...HeroFields.keywordBadge,
      contentEditable: true,
    },
    ctaText: {
      ...HeroFields.ctaText,
      contentEditable: true,
    },
    ctaUrl: HeroFields.ctaUrl,
    secondaryCtaText: HeroFields.secondaryCtaText,
    secondaryCtaUrl: HeroFields.secondaryCtaUrl,
    trustIndicators: HeroFields.trustIndicators,
    backgroundImage: {
      ...HeroFields.backgroundImage,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Background Image" />,
    },
    imagePosition: HeroFields.imagePosition,
    imageMotion: HeroFields.imageMotion,
    photos: {
      ...HeroFields.photos,
      arrayFields: {
        ...HeroFields.photos.arrayFields,
        imageUrl: {
          ...HeroFields.photos.arrayFields.imageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Photo" />,
        },
      },
    } as any,
    showCopy: HeroFields.showCopy,
    showSlideshowDots: HeroFields.showSlideshowDots,
    showSlideshowPause: HeroFields.showSlideshowPause,
    slideSeconds: HeroFields.slideSeconds,
    overlay: HeroFields.overlay,
    textColor: HeroFields.textColor,
    layout: HeroFields.layout,
    variant: HeroFields.variant,
    heroForm: HeroFields.heroForm,
  },
  /**
   * Only show controls the chosen layout actually reads. A field that does nothing is worse
   * than a missing one: the pro toggles it, sees no change, and stops trusting the editor.
   *
   * Filters the `fields` it is handed rather than rebuilding from HeroFields, so the
   * dashboard's own field overrides (ImageUploader and friends) survive.
   */
  resolveFields: (data, { fields, metadata }) => {
    const props = (data.props || {}) as Partial<HeroProps>;
    const layout = props.layout || "centered";
    // The Settings label wins over the saved page value by design — say so rather than
    // offering a box that silently discards what you type.
    const visible = withBusinessCtaOverride(
      { ...(fields as Record<string, unknown>) },
      "ctaText",
      metadata,
    );

    // The side card is a split-layout feature; nothing else renders a form.
    if (layout !== "split") {
      delete visible.heroForm;
    }

    // Showcase owns the photo rotation and the copy toggle.
    if (layout !== "showcase") {
      delete visible.photos;
      delete visible.showCopy;
      delete visible.showSlideshowDots;
      delete visible.showSlideshowPause;
      delete visible.slideSeconds;
    }

    if (layout === "showcase") {
      // Dots and pacing act on the rotation; with 0–1 photos there is nothing to rotate.
      if ((props.photos || []).filter((photo) => photo?.imageUrl).length < 2) {
        delete visible.showSlideshowDots;
        delete visible.showSlideshowPause;
        delete visible.slideSeconds;
      }
      // Showcase fills the screen with `photos`; the single background image is only its
      // fallback, and showing both invites the pro to set the one that loses.
      if ((props.photos || []).some((photo) => photo?.imageUrl)) {
        delete visible.backgroundImage;
        delete visible.imagePosition;
      }

      // With the copy hidden there is no text to style, position or colour.
      if (props.showCopy === "no") {
        delete visible.variant;
        delete visible.overlay;
        delete visible.textColor;
        delete visible.subheadline;
        delete visible.ctaText;
        delete visible.ctaUrl;
        delete visible.secondaryCtaText;
        delete visible.secondaryCtaUrl;
        delete visible.trustIndicators;
        delete visible.keywordBadge;
      } else if (props.variant !== "proof") {
        // Trust indicators are the proof variant's whole point.
        delete visible.trustIndicators;
      }
    }

    return visible as typeof fields;
  },
  defaultProps: {
    headline: "", // Let render handle the default
    subheadline: "", // Let render handle the default
    ctaText: "Get Free Quote",
    ctaUrl: "/contact", // Link to the contact page, not phone
    secondaryCtaText: "", // Let render handle the phone CTA
    secondaryCtaUrl: "", // Let render handle the phone URL
    trustIndicators: [
      { text: "Licensed & Insured" },
      { text: "Free Estimates" },
      { text: "Satisfaction Guaranteed" },
    ],
    backgroundImage:
      "https://imagedelivery.net/a_gggTgujA-YoVa4zU3kuA/c61d7e67-3cb0-401b-0c52-536d2f7f5400/public",
    overlay: "dark",
    textColor: "auto",
    layout: "centered",
    variant: "clean",
    photos: [],
    showCopy: "yes",
    showSlideshowDots: "no",
    showSlideshowPause: "no",
    slideSeconds: 6,
    imagePosition: "center",
    imageMotion: "none",
    heroForm: "lead",
    keywordBadge: "",
  },
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    // Get phone number - check both 'phone' and 'telephone' property names
    const businessPhone = business?.phone || business?.telephone;

    // Resolve the effective primary CTA via the shared precedence helper.
    // Owner-set Site.ctaPrimaryLabel wins over saved AI text; falls back to
    // system default ("Call Now"). Phone-prefixed labels still bias to tel:.
    const finalCtaText = resolveBusinessCtaText(props.ctaText, business);
    // The card picked in Business settings decides this, not the label's wording.
    const wantsPhoneUrl = ctaPosture(business, finalCtaText) === "phone" && Boolean(businessPhone);

    /**
     * The two buttons must offer two different actions.
     *
     * The secondary used to be the phone number unconditionally, so a business
     * whose CTA posture is phone-first ("Call Now", which is also the system
     * default) shipped a hero with two call buttons: "Call Now" beside
     * "Call Us: (508) 272-1139", both pointing at the same tel: link.
     *
     * So the secondary is whichever of {call, form} the primary is not.
     */
    const secondaryCta = (): { text: string; url: string } | undefined => {
      if (wantsPhoneUrl) {
        // Primary already calls, so this is the way through for people who
        // won't phone. Deliberately neutral: the owner chose a phone-first
        // card, so borrowing "Get a Free Quote" from another card would put an
        // offer on their site they never agreed to.
        return { text: SECONDARY_FORM_CTA_TEXT, url: "/contact" };
      }
      if (businessPhone) {
        return { text: `Call Us: ${formatPhoneDisplay(businessPhone)}`, url: `tel:${businessPhone}` };
      }
      // No phone, and the primary is the form — one button is the honest answer
      // rather than inventing a number.
      return undefined;
    };

    const secondary = secondaryCta();

    // Default values if not provided in props
    // Primary CTA: phone-tel for "Call"-prefixed labels (when phone exists), else #contact
    const resolvedProps = {
      ...props,
      headline:
        props.headline ||
        (business?.city
          ? `Best Service in ${business.city}`
          : "Professional Service You Can Trust"),
      subheadline:
        props.subheadline ||
        (business?.city
          ? `Fast, reliable service in ${business.city}, ${business.state || ""}.`
          : "Fast, reliable service from certified technicians."),
      ctaText: finalCtaText,
      // The owner's CTA card decides where the primary button goes. The saved
      // prop is a generator default ("/contact" from PAGE_LAYOUTS), not a
      // choice anyone made, so letting it win produced a "Call Now" button that
      // opened the contact page.
      ctaUrl: wantsPhoneUrl
        ? `tel:${businessPhone}`
        : contactUrl(props.ctaUrl),
      secondaryCtaText: secondary?.text,
      secondaryCtaUrl: secondary?.url,
      variant: props.variant || "clean",
      hasTrackingPhone: !!(business as JobsiteBusinessContext | undefined)?.hasTrackingPhone,
      // Booking-widget variant of the split-layout side card resolves the same
      // injected metadata the BookingWidget section uses.
      bookingSlug: business?.slug,
      bookingApiUrl: windowConfig?.apiUrl,
      businessName: business?.name,
    };

    return <Hero id={id} {...resolvedProps} />;
  },
};
